import { StorageHandler, sendStorageEvent } from "@/lib/storage";
import { getWindow } from "@/lib/window";
import { isEqual } from "lodash-es";

export interface TabState {
  tabs?: string[];
  activeTabId?: string;
}

export type TabStateStorage = ReturnType<typeof buildTabStateStorage>;

const storageKey = (suffix?: boolean) => `pyne-tab-state${suffix ? "preview" : ""}`;
const currentTabIdKey = "pyne-current-tab-id";

export const buildTabStateStorage = (suffix: boolean, storage: StorageHandler, sessionStorage: StorageHandler) => {
  const self = () => buildTabStateStorage(suffix, storage, sessionStorage);

  return {
    key: () => {
      return storageKey(suffix);
    },
    get: () => {
      return storage.get(storageKey(suffix), {});
    },
    clear: () => {
      storage.remove(storageKey(suffix));
    },
    set: (state: Partial<TabState>) => {
      storage.set(storageKey(suffix), { ...self().get(), ...state });
    },
    addTab: (tabId: string) => {
      self().set({
        tabs: [...self().getTabs(), tabId],
      });
    },
    setCurrentTabId: (tabId: string) => {
      sessionStorage.set(currentTabIdKey, tabId);
    },
    setActiveTab: (tabId?: string) => {
      const oldValue = self().get();
      self().set({ activeTabId: tabId });
      const newValue = self().get();
      sendStorageEvent(storageKey(suffix), JSON.stringify(oldValue), JSON.stringify(newValue));
    },
    clearActiveTab: () => {
      const oldValue = self().get();
      self().set({ activeTabId: undefined });
      const newValue = self().get();
      sendStorageEvent(storageKey(suffix), JSON.stringify(oldValue), JSON.stringify(newValue));
    },
    clearTab: (tabId: string) => {
      const oldValue = self().get();
      self().removeTab(tabId);
      self().set({ activeTabId: undefined });
      const newValue = self().get();
      sendStorageEvent(storageKey(suffix), JSON.stringify(oldValue), JSON.stringify(newValue));
    },
    removeTab: (tabId: string) => {
      self().set({
        tabs: self()
          .getTabs()
          .filter((id) => id !== tabId),
      });
    },
    getCurrentTabId: (): string | undefined => {
      return sessionStorage.get(currentTabIdKey, undefined);
    },
    getActiveTabId: (): string | undefined => {
      return self().get().activeTabId;
    },
    getTabs: (): string[] => {
      return self().get().tabs || [];
    },
    onActiveTabChange: (callback: (activeTabId?: string) => void) => {
      let lastActiveTabId: string | undefined = self().get().activeTabId;

      const activeTabChangedCallback = () => {
        const activeTabId = self().get().activeTabId;
        if (activeTabId !== lastActiveTabId) {
          lastActiveTabId = activeTabId;
          callback(activeTabId);
        }
      };

      const storageListener = (event: StorageEvent) => {
        if (event.key === storageKey(suffix)) {
          const oldState = JSON.parse(event.oldValue ?? "{}");
          const newState = JSON.parse(event.newValue ?? "{}");

          if (oldState.activeTabId !== newState.activeTabId) {
            activeTabChangedCallback();
          }
        }
      };

      getWindow().addEventListener("storage", storageListener);
      getWindow().addEventListener("unload", () => {
        getWindow().removeEventListener("storage", storageListener);
      });
    },
    onTabsChange: (callback: (tabs: string[]) => void) => {
      let lastTabs: string[] = [];

      const updateTabs = () => {
        const tabs = self().getTabs();
        if (!isEqual(tabs, lastTabs)) {
          lastTabs = tabs;
          callback(tabs);
        }
      };

      updateTabs();

      const storageListener = (event: StorageEvent) => {
        if (event.key === storageKey(suffix)) {
          const oldState = JSON.parse(event.oldValue ?? "{}");
          const newState = JSON.parse(event.newValue ?? "{}");

          if (!isEqual(oldState.tabs, newState.tabs)) {
            updateTabs();
          }
        }
      };

      getWindow().addEventListener("storage", storageListener);
      getWindow().addEventListener("unload", () => {
        getWindow().removeEventListener("storage", storageListener);
      });
    },
  };
};
